import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import styles from './Combobox.module.css';

export interface ComboboxOption {
  id: string;
  label: string;
  /** A second, quieter line: "ISO 400 · C-41 · 35mm, 120". */
  detail?: string;
}

export interface ComboboxGroup {
  label: string;
  options: ComboboxOption[];
}

/** A last option that acts on the typed text: "Add ‘Portra 800’ as a new stock…". */
export interface ComboboxAction {
  label: string;
  onChoose: () => void;
}

export interface ComboboxProps {
  label: string;
  /** The typed text. The parent filters `groups` from it. */
  query: string;
  onQueryChange: (query: string) => void;
  groups: ComboboxGroup[];
  onSelect: (option: ComboboxOption) => void;
  action?: ComboboxAction | null;
  /** Every match, when `groups` holds only the first few. */
  total?: number;
  /** "12 stocks match": what the live region says. */
  describeCount?: (count: number) => string;
  hint?: string;
  placeholder?: string;
  /** Marks the input as failing validation. */
  invalid?: boolean;
  /** The id of the message saying what's wrong, read with the input. */
  errorId?: string;
}

const defaultDescribe = (n: number) => `${n} ${n === 1 ? 'match' : 'matches'}`;

/**
 * An editable combobox with a grouped listbox popup, as the WAI-ARIA 1.2
 * pattern ("list autocomplete with automatic selection") describes:
 *
 * - Typing opens the list and highlights its first option, so Enter picks
 *   the best match.
 * - Down and Up move through the options (wrapping), and open the list when
 *   it's closed. Enter picks the highlighted option. Escape closes the list,
 *   or clears the text when it's already closed. Tab leaves without picking.
 * - Focus stays in the input; the highlighted option is `aria-activedescendant`
 *   and scrolled into view. A polite live region says how many match.
 *
 * It filters nothing itself: the parent turns `query` into `groups`.
 */
export function Combobox({
  label,
  query,
  onQueryChange,
  groups,
  onSelect,
  action,
  total,
  describeCount = defaultDescribe,
  hint,
  placeholder,
  invalid,
  errorId,
}: ComboboxProps) {
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const hintId = `${baseId}-hint`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const options = groups.flatMap((g) => g.options);
  const count = options.length + (action ? 1 : 0);
  const matches = total ?? options.length;
  // Keep the highlight on an option that's still there.
  const activeIndex = active < count ? active : count - 1;

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${baseId}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, baseId]);

  function choose(index: number) {
    if (index < options.length) onSelect(options[index]);
    else action?.onChoose();
    setOpen(false);
    setActive(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (count === 0) return setOpen(true);
        if (!open || e.altKey) {
          setOpen(true);
          if (activeIndex < 0 && !e.altKey) setActive(0);
          return;
        }
        setActive((activeIndex + 1) % count);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (count === 0) return setOpen(true);
        setOpen(true);
        setActive(!open || activeIndex < 0 ? count - 1 : (activeIndex - 1 + count) % count);
        return;
      case 'Enter':
        if (open && activeIndex >= 0) {
          e.preventDefault();
          choose(activeIndex);
        }
        return;
      case 'Escape':
        if (open) {
          e.preventDefault();
          e.stopPropagation(); // a dialog around us stays open
          setOpen(false);
        } else if (query) {
          e.preventDefault();
          e.stopPropagation();
          onQueryChange('');
        }
        return;
      case 'Tab':
        setOpen(false);
        return;
    }
  }

  const renderOption = (i: number, content: ReactNode, key: string, extraClass = '') => (
    // Options aren't focusable and have no keys of their own: in the combobox
    // pattern focus stays in the input, which handles the keys, and
    // aria-activedescendant points here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
    <div
      key={key}
      id={optionId(i)}
      role="option"
      aria-selected={i === activeIndex}
      className={[styles.option, extraClass, i === activeIndex ? styles.active : ''].filter(Boolean).join(' ')}
      // Keep focus in the input; the click then picks.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => choose(i)}
    >
      {content}
    </div>
  );
  // Where each group's options start in the flat list.
  const offsets = groups.map((_, gi) => groups.slice(0, gi).reduce((n, g) => n + g.options.length, 0));

  const truncated = matches > options.length;
  const status = !open
    ? ''
    : [describeCount(matches), truncated ? `showing the first ${options.length}` : null, action ? action.label : null]
        .filter(Boolean)
        .join('. ');

  return (
    <div className={styles.combobox}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      <div className={styles.field}>
        <input
          id={inputId}
          type="text"
          role="combobox"
          className={styles.input}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-describedby={[hint ? hintId : '', invalid && errorId ? errorId : ''].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid || undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => setOpen(false)}
        />
        <div className={styles.popup} hidden={!open}>
          <div id={listboxId} role="listbox" aria-label={label} className={styles.listbox} hidden={count === 0}>
            {groups.map((group, gi) =>
              group.options.length === 0 ? null : (
                <div key={group.label} role="group" aria-labelledby={`${baseId}-group-${gi}`}>
                  <div id={`${baseId}-group-${gi}`} role="presentation" className={styles.groupLabel}>
                    {group.label}
                  </div>
                  {group.options.map((option, oi) =>
                    renderOption(
                      offsets[gi] + oi,
                      <>
                        <span className={styles.optionLabel}>{option.label}</span>
                        {option.detail && <span className={styles.optionDetail}>{option.detail}</span>}
                      </>,
                      option.id,
                    ),
                  )}
                </div>
              ),
            )}
            {action && renderOption(options.length, action.label, '__action', styles.action)}
          </div>
          {(truncated || count === 0) && (
            <p className={styles.note}>
              {count === 0 ? 'Nothing matches.' : `Showing ${options.length} of ${matches}. Keep typing to narrow it down.`}
            </p>
          )}
        </div>
      </div>
      <div role="status" aria-live="polite" className={styles.srOnly}>
        {status}
      </div>
    </div>
  );
}
